import { produce } from 'immer';

import { convertToMavenirFlatTreeData } from '@utils/treeFormatter/treeFormatter';
import {
	getMoIdsPath,
	isMavenirNodeObject,
	isMavenirNodeObjectsArray,
	prepareTreeSelectValues,
} from '@components/routes/CmModule/ConfigurationComparison/MOTreeMavenir/FlatTreeMavenir/utils';

import { ICategoryPath, IFlatMoTreeNode } from '@interfaces/components/tree';
import { IUpdateMavenirConfigDto } from './FlatTreeMavenir/interfaces';

import { FLAT_NODE_PATH_SEPARATOR as separator } from '@components/common/FlatTree/constants';

interface IPreparedPaths {
	params: string[];
	includeAllParams: boolean;
	path: string;
}

const getParameters = (
	data: IFlatMoTreeNode[],
	isInverseCheck: boolean,
): Map<string, IPreparedPaths> => {
	return data.reduce((acc, node) => {
		const { isChecked, key, keysPath, countOfChildren } = node;

		const path: string = keysPath || '';

		const isParameterAllowed = isInverseCheck ? !isChecked : isChecked;

		if (!isMavenirNodeObject(node) && isParameterAllowed) {
			const parameterRegex = new RegExp(`${separator}${key}$`);
			const parentPath = path.replace(parameterRegex, '');
			const existingParams = acc.get(parentPath);

			const parameters = {
				includeAllParams: existingParams?.params.length === countOfChildren,
				path: parentPath,
				params: existingParams
					? [...existingParams.params, key]
					: [key],
			};

			acc.set(parentPath, parameters);
		}

		return acc;
	}, new Map<string, IPreparedPaths>());
};

const getKeyPosition = (node: IFlatMoTreeNode) => {
	const pattern = new RegExp(`(?=(/${node.key}(/|$)))`, 'g');
	const matches = node.path.match(pattern) || [];

	return matches?.length > 1 ? matches.length : 0;
};

const getPathsWithOptions = (data: IFlatMoTreeNode[]) => {
	return data
		.filter((node) => isMavenirNodeObjectsArray(node))
		.map((node) => {
			return ({
				optionPath: node.keysPath || '',
				key: node.key,
				position: getKeyPosition(node),
				categoryPath: node.key + getMoIdsPath(node.checkboxList || []),
			});
		});

};

export const preparePaths = (
	data: IFlatMoTreeNode[],
	isInverseCheck = false,
): Array<ICategoryPath> => {
	let result: ICategoryPath[] = [];

	const parameters = getParameters(data, isInverseCheck);
	const pathsWithOptions = getPathsWithOptions(data);

	parameters
		.forEach(({ includeAllParams, params, path }) => {
				let categoryId = path;

				for (let index = 0; index < pathsWithOptions.length; index++) {
					const { key, categoryPath, position, optionPath } = pathsWithOptions[index];

					if (!path.startsWith(optionPath)) {
						continue;
					}

					if (!position) {
						const keys = categoryId.split(separator)
							.filter(Boolean)
							.map((item) => key === item ? categoryPath : item);

						categoryId = keys.join(separator);

						continue;
					}

					let count = 0;

					const categoryIdParts = categoryId.split(separator).filter(Boolean);

					for (let i = 0; i < categoryIdParts.length; i++) {
						if (categoryIdParts[i] === key) {
							count++;

							if (count === position) {
								categoryIdParts[i] = categoryPath;

								break;
							}
						}
					}

					categoryId = categoryIdParts.join(separator);
				}

				result = [...result, { params, includeAllParams, categoryId }];
			},
		);

	return result;
};

export const setCheckboxListForPaths = ({
	setData,
	node,
	data,
}: IUpdateMavenirConfigDto) => {
	setData(
		produce((draftState) => {
			for (let index = 0; index < draftState.length; index++) {
				const path = draftState[index].path;
				const pathMatchedElement = data[path];

				if (!pathMatchedElement || path === node.path) {
					continue;
				}

				const selectValues = prepareTreeSelectValues(pathMatchedElement);

				draftState[index].checkboxList = selectValues;
			}
		}),
	);
};

export const clearPathFromMoIds = (path: string) => {
	return path.replace(/\/{[^}]*}/g, '');
};

export const getMoSkippedParameters = (data: IFlatMoTreeNode[]) => {
	return data
		.filter((node) => !node.nodeType && !node.isChecked)
		.map((node) => node.path);
};

export const createSettledPathHandler = ({
	setData,
	node,
	data,
}: IUpdateMavenirConfigDto) => {
	const parentPath = node.path.split('/').slice(0, -1).join('/');

	const preparedData = convertToMavenirFlatTreeData({
		data: data ? [data.moTree] : [],
		isSingleSelectMode: true,
		parentPath: parentPath,
		depth: node.depth,
		defaultCheckboxList: node.checkboxList,
		isDefaultNodeChecked: true,
	});

	return setData(
		produce((draftState) => {
			const start = draftState.findIndex((item) => item.id === node.id);
			const end = start + node.countOfChildren;

			const diff = draftState[start].countOfChildren - node.countOfChildren;
			const deleteCount = node.countOfChildren + 1;

			draftState.splice(start, deleteCount, ...preparedData);

			for (let index = 0; index < draftState.length; index++) {
				if (index < end) {
					draftState[index].countOfChildren = draftState[index].countOfChildren + diff;
				}

				draftState[index].order = index;
			}
		}),
	);
};

